import { DoneInvokeEvent, assign, createMachine } from 'xstate'
import { DELETEResponse, IRejection, IWarning } from '@cbgms/api'

export interface DeleteContext {
  deleteReason?: string
  requiresConfirmation: boolean
  rejections?: IRejection[]
  warnings?: IWarning[]
}

export type DeleteState =
  | {
      value: 'idle'
      context: DeleteContext & { rejections: undefined; warnings: undefined }
    }
  | {
      value: 'verifying'
      context: DeleteContext & { rejections: undefined; warnings: undefined }
    }
  | {
      value: 'checking'
      context: DeleteContext & { rejections: IRejection[]; warnings: IWarning[] }
    }
  | {
      value: 'containsRejections'
      context: DeleteContext & { rejections: IRejection[]; warnings: IWarning[] }
    }
  | {
      value: 'containsWarnings'
      context: DeleteContext & { rejections: []; warnings: IWarning[] }
    }
  | {
      value: 'requiresConfirmation'
      context: DeleteContext & { rejections: never; warnings: never }
    }
  | {
      value: 'deleting'
      context: DeleteContext & { rejections: IRejection[]; warnings: IWarning[] }
    }
  | {
      value: 'success'
      context: DeleteContext & { rejections: IRejection[]; warnings: IWarning[] }
    }

export type DeleteEvents =
  | { type: 'START' }
  | { type: 'CANCEL' }
  | {
      type: 'CONFIRM'
      deleteReason?: string
    }

export const deleteMachine = createMachine<DeleteContext, DeleteEvents, DeleteState>(
  {
    id: 'deleteConfirmation',
    initial: 'idle',
    context: {
      deleteReason: undefined,
      requiresConfirmation: true,
      warnings: undefined,
      rejections: undefined
    },
    states: {
      idle: {
        on: {
          START: 'verifying'
        }
      },
      verifying: {
        invoke: {
          src: 'checkIfDeleteIsPossible',
          onDone: {
            target: 'checking',
            actions: assign({
              rejections: (_, event: DoneInvokeEvent<DELETEResponse>) => event.data.Data.Rejections,
              warnings: (_, event: DoneInvokeEvent<DELETEResponse>) => event.data.Data.Warnings
            })
          }
        }
      },
      checking: {
        always: [
          {
            target: 'containsRejections',
            cond: 'containsRejections'
          },
          {
            target: 'containsWarnings',
            cond: 'containsWarnings'
          },
          {
            target: 'requiresConfirmation',
            cond: 'requiresConfirmation'
          },
          { target: 'deleting' }
        ]
      },
      containsRejections: {
        on: {
          CANCEL: 'idle',
          CONFIRM: undefined
        }
      },
      containsWarnings: {
        on: {
          CANCEL: 'idle',
          CONFIRM: {
            target: 'deleting',
            actions: assign({
              deleteReason: (_, event) => event.deleteReason
            })
          }
        }
      },
      requiresConfirmation: {
        on: {
          CANCEL: 'idle',
          CONFIRM: {
            target: 'deleting',
            actions: assign({
              deleteReason: (_, event) => event.deleteReason
            })
          }
        }
      },
      deleting: {
        invoke: {
          src: 'handleDelete',
          onDone: {
            target: 'success'
          }
        }
      },
      success: {
        invoke: {
          src: 'handleDeleteSuccess'
        },
        type: 'final'
      }
    }
  },
  {
    guards: {
      containsRejections: context => {
        const rejectionsLength = context.rejections?.length

        return rejectionsLength !== undefined && rejectionsLength > 0
      },
      containsWarnings: context => {
        const warningsLength = context.warnings?.length

        return warningsLength !== undefined && warningsLength > 0
      },
      requiresConfirmation: context => context.requiresConfirmation
    }
  }
)
